import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  doc,
  getDoc,
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  arrayRemove,
  arrayUnion
} from 'firebase/firestore'
import { db } from '../../firebase'
import PropTypes from 'prop-types'
import CreditIcon from '../CreditIcon'
import InjuryIcon from '../InjuryIcon'
import SeasonCreationBanner from '../SeasonCreationBanner'
import { canCreateFaction } from '../../utils/seasonGate'
import {
  buildFactionMemberFields,
  getFactionStoredWrestlers
} from '../../utils/factionWrestlers'
import { buildFactionLineupUpdateAfterSwap } from '../../utils/factionLineup'
import {
  getAssignedWrestlerIds,
  getRosterAvailableWrestlers
} from '../../utils/wrestlerAvailability'

const getEntryId = entry => entry?.wrestlerId || entry?.id || null

const normalizeStoredCost = value => {
  const parsedValue = Number(value)
  return Number.isFinite(parsedValue) ? parsedValue : 0
}

const getFactionMembersForForm = faction => {
  const costs = Array.isArray(faction?.costs) ? faction.costs : []
  const fallbackMembers = getFactionStoredWrestlers(faction)
  const fallbackById = new Map(
    fallbackMembers.map(member => [getEntryId(member), member])
  )

  if (costs.length === 0) {
    return fallbackMembers
      .map(member => {
        const id = getEntryId(member)
        if (!id) return null

        return {
          ...member,
          id,
          wrestlerId: id,
          cost: normalizeStoredCost(member.cost)
        }
      })
      .filter(Boolean)
  }

  return costs
    .map(entry => {
      const id = getEntryId(entry)
      if (!id) return null

      const fallback = fallbackById.get(id) || {}

      return {
        ...fallback,
        ...entry,
        id,
        wrestlerId: id,
        cost: normalizeStoredCost(entry.cost ?? fallback.cost),
        startScore: entry.startScore ?? fallback.startScore ?? 0,
        currentScore:
          entry.currentScore ?? fallback.currentScore ?? fallback.score ?? 0,
        accumulatedPoints:
          entry.accumulatedPoints ?? fallback.accumulatedPoints ?? 0,
        status: entry.status ?? fallback.status ?? null
      }
    })
    .filter(Boolean)
}

const FactionForm = ({
  initialOwnerId,
  rosterId,
  initialRosterName,
  roster,
  maxWrestlers,
  initialCredits = 300,
  minMaleWrestlers = 0,
  minFemaleWrestlers = 0,
  onlyMainRoster = false,
  onlyNXT = false,
  existingFaction,
  onSuccess,
  onCancel,
  allUsers,
  wrestlers,
  factions
}) => {
  const maxSlots = useMemo(
    () => parseInt(maxWrestlers, 10) || 8,
    [maxWrestlers]
  )
  const emptySlots = useMemo(
    () =>
      Array(maxSlots)
        .fill()
        .map(() => ({ wrestlerId: '', cost: 0 })),
    [maxSlots]
  )
  const buildPaddedSlots = useCallback(
    factionData => {
      const storedMembers = getFactionMembersForForm(factionData)

      return Array(maxSlots)
        .fill()
        .map((_, i) => {
          const member = storedMembers[i]

          return member
            ? { wrestlerId: member.id, cost: member.cost ?? 0 }
            : { wrestlerId: '', cost: 0 }
        })
    },
    [maxSlots]
  )

  const initialFormState = useMemo(
    () => ({
      name: '',
      roster: initialRosterName,
      ownerUid: initialOwnerId,
      gameType: 'Competitivo',
      costs: emptySlots
    }),
    [emptySlots, initialOwnerId, initialRosterName]
  )

  const [form, setForm] = useState(initialFormState)
  const [message, setMessage] = useState('')
  const [editingId, setEditingId] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [searchWrestler, setSearchWrestler] = useState('')

  const selectedCostRows = useMemo(
    () =>
      form.costs
        .map((cost, originalIdx) => ({
          ...cost,
          originalIdx,
          wrestler: wrestlers.find(wrestler => wrestler.id === cost.wrestlerId)
        }))
        .filter(cost => cost.wrestlerId !== ''),
    [form.costs, wrestlers]
  )
  const selectedCount = selectedCostRows.length
  const emptySlotsCount = form.costs.length - selectedCount

  const resetForm = useCallback(() => {
    setEditingId(null)
    setForm(initialFormState)
    setMessage('')
    setSearchWrestler('')
  }, [initialFormState])

  // Inizializza il form
  useEffect(() => {
    if (existingFaction) {
      setEditingId(existingFaction.id)
      setForm({
        name: existingFaction.name || '',
        roster: existingFaction.roster || initialRosterName,
        ownerUid: existingFaction.ownerUid || initialOwnerId,
        gameType: existingFaction.gameType || 'Competitivo',
        costs: buildPaddedSlots(existingFaction)
      })
    } else {
      resetForm()
    }
  }, [
    buildPaddedSlots,
    existingFaction,
    initialOwnerId,
    initialRosterName,
    resetForm
  ])

  const updateField = (key, value) =>
    setForm(prev => ({ ...prev, [key]: value }))

  const updateCost = (idx, key, value) => {
    setForm(prev => ({
      ...prev,
      costs: prev.costs.map((slot, slotIdx) =>
        slotIdx === idx
          ? { ...slot, [key]: key === 'cost' ? Number(value) : value }
          : slot
      )
    }))
  }

  const normalizeCost = value => {
    const parsedValue = parseInt(value, 10)
    return Number.isNaN(parsedValue) || parsedValue < 1 ? 1 : parsedValue
  }

  const handleCostChange = (idx, value) => {
    updateCost(idx, 'cost', normalizeCost(value))
  }

  const adjustCost = (idx, delta) => {
    const currentCost = normalizeCost(form.costs[idx]?.cost)
    updateCost(idx, 'cost', Math.max(1, currentCost + delta))
  }

  const assignWrestlerToSlot = (idx, wrestlerId, cost = 1) => {
    setForm(prev => ({
      ...prev,
      costs: prev.costs.map((slot, slotIdx) =>
        slotIdx === idx ? { wrestlerId, cost: Number(cost) } : slot
      )
    }))
  }

  const clearWrestlerSlot = idx => {
    setForm(prev => ({
      ...prev,
      costs: prev.costs.map((slot, slotIdx) =>
        slotIdx === idx ? { wrestlerId: '', cost: 0 } : slot
      )
    }))
  }

  const factionBudget = existingFaction?.initialCredits || initialCredits
  const totalCost = useMemo(
    () =>
      form.costs.reduce(
        (sum, m) => sum + (m.wrestlerId ? m.cost || 0 : 0),
        0
      ),
    [form.costs]
  )
  const creditsLeft = useMemo(
    () => factionBudget - totalCost,
    [factionBudget, totalCost]
  )
  const selectedWrestlerIds = useMemo(
    () => form.costs.filter(c => c.wrestlerId !== '').map(c => c.wrestlerId),
    [form.costs]
  )
  const occupiedWrestlerIds = useMemo(() => {
    return getAssignedWrestlerIds(factions, {
      excludedFactionId: editingId
    })
  }, [editingId, factions])
  const selectedWrestlers = useMemo(
    () =>
      selectedWrestlerIds
        .map(wrestlerId =>
          wrestlers.find(wrestler => wrestler.id === wrestlerId)
        )
        .filter(Boolean),
    [selectedWrestlerIds, wrestlers]
  )
  const selectedMaleCount = useMemo(
    () =>
      selectedWrestlers.filter(wrestler => wrestler.gender === 'Uomo').length,
    [selectedWrestlers]
  )
  const selectedFemaleCount = useMemo(
    () =>
      selectedWrestlers.filter(wrestler => wrestler.gender === 'Donna').length,
    [selectedWrestlers]
  )

  const isBrandAllowed = useCallback(
    wrestler => {
      if (onlyNXT) return wrestler.brand === 'NXT'
      if (onlyMainRoster)
        return wrestler.brand === 'Raw' || wrestler.brand === 'SmackDown'
      return true
    },
    [onlyMainRoster, onlyNXT]
  )

  const validateForm = () => {
    if (!form.name || form.name.trim().length < 3) {
      setMessage('Il nome della fazione deve essere di almeno 3 caratteri')
      return false
    }
    if (!form.ownerUid) {
      setMessage('Proprietario non valido')
      return false
    }
    if (creditsLeft < 0) {
      setMessage('Crediti insufficienti')
      return false
    }

    // Unique name check
    const nameExists = factions.some(
      f =>
        f.name.trim().toLowerCase() === form.name.trim().toLowerCase() &&
        (!editingId || f.id !== editingId)
    )
    if (nameExists) {
      setMessage('Nome fazione già esistente')
      return false
    }

    // No duplicate wrestlers
    const wrestlerIds = form.costs
      .filter(c => c.wrestlerId !== '')
      .map(c => c.wrestlerId)
    const uniqueWrestlers = new Set(wrestlerIds)
    if (uniqueWrestlers.size !== wrestlerIds.length) {
      setMessage('Non puoi selezionare lo stesso wrestler più di una volta')
      return false
    }

    const occupiedSelection = wrestlerIds.find(wrestlerId =>
      occupiedWrestlerIds.has(wrestlerId)
    )
    if (occupiedSelection) {
      const occupiedWrestler = wrestlers.find(
        wrestler => wrestler.id === occupiedSelection
      )
      setMessage(
        `${
          occupiedWrestler?.name || 'Questo wrestler'
        } e gia presente in un'altra fazione`
      )
      return false
    }

    const invalidBrandSelection = selectedWrestlers.find(
      wrestler => !isBrandAllowed(wrestler)
    )
    if (invalidBrandSelection) {
      setMessage(
        `${invalidBrandSelection.name} non rispetta i vincoli del brand del roster`
      )
      return false
    }

    if (selectedMaleCount < minMaleWrestlers) {
      setMessage(`Devi selezionare almeno ${minMaleWrestlers} wrestler uomo`)
      return false
    }

    if (selectedFemaleCount < minFemaleWrestlers) {
      setMessage(`Devi selezionare almeno ${minFemaleWrestlers} wrestler donna`)
      return false
    }

    // All costs must be >= 0
    for (const c of form.costs) {
      if (c.wrestlerId && (typeof c.cost !== 'number' || c.cost < 0)) {
        setMessage('Il costo di ogni wrestler deve essere un numero positivo')
        return false
      }
    }
    return true
  }

  const handleSubmit = async e => {
    e.preventDefault()
    setMessage('')
    if (!editingId && !canOwnerCreateFaction) {
      setMessage(
        'La creazione delle fazioni e bloccata fino al 1 maggio alle 12:00.'
      )
      return
    }
    if (!validateForm()) return
    setSubmitting(true)

    try {
      const existingFactionDoc = editingId
        ? factions.find(f => f.id === editingId) || existingFaction
        : null
      const existingMembers = existingFactionDoc
        ? getFactionMembersForForm(existingFactionDoc)
        : []
      const existingMap = new Map(
        existingMembers.map(member => [member.id, member])
      )
      const previousSlots = existingFactionDoc
        ? buildPaddedSlots(existingFactionDoc)
        : emptySlots
      const hasMemberChanges = previousSlots.some(
        (slot, index) =>
          (slot?.wrestlerId || '') !== (form.costs[index]?.wrestlerId || '')
      )
      const replacementPairs = previousSlots.reduce(
        (acc, slot, index) => {
          const previousId = slot?.wrestlerId || ''
          const nextId = form.costs[index]?.wrestlerId || ''

          if (previousId && nextId && previousId !== nextId) {
            acc.released.push(previousId)
            acc.assigned.push(nextId)
          }

          return acc
        },
        { released: [], assigned: [] }
      )
      const replacementMap = replacementPairs.released.reduce(
        (acc, previousId, index) => {
          const nextId = replacementPairs.assigned[index]
          if (previousId && nextId) {
            acc[previousId] = nextId
          }
          return acc
        },
        {}
      )

      const costsData = (
        await Promise.all(
          form.costs.map(async (slot, index) => {
            if (!slot.wrestlerId) return null

            const existingMember = existingMap.get(slot.wrestlerId)
            if (existingMember) {
              return {
                wrestlerId: slot.wrestlerId,
                cost: normalizeStoredCost(slot.cost),
                startScore: existingMember.startScore ?? 0,
                currentScore:
                  existingMember.currentScore ??
                  existingMember.score ??
                  existingMember.startScore ??
                  0,
                accumulatedPoints: existingMember.accumulatedPoints || 0,
                status: existingMember.status || null
              }
            }

            const previousMember = existingMembers[index] || null
            const wSnap = await getDoc(doc(db, 'wrestlers', slot.wrestlerId))
            const start = wSnap.exists() ? wSnap.data().score || 0 : 0

            return {
              wrestlerId: slot.wrestlerId,
              cost: normalizeStoredCost(slot.cost),
              startScore: start,
              currentScore: start,
              accumulatedPoints: 0,
              status:
                previousMember?.id &&
                previousMember.id !== slot.wrestlerId &&
                hasMemberChanges
                  ? previousMember.status || null
                  : null
            }
          })
        )
      ).filter(Boolean)
      const updatedMembers = costsData.map(member => ({
        ...member,
        id: member.wrestlerId,
        wrestlerId: member.wrestlerId
      }))
      const lineupUpdate =
        editingId && hasMemberChanges
          ? buildFactionLineupUpdateAfterSwap({
              faction: existingFactionDoc,
              roster,
              updatedWrestlers: updatedMembers,
              replacements: replacementMap,
              now: new Date()
            })
          : null
      const memberFields = buildFactionMemberFields(updatedMembers)

      const payload = {
        name: form.name,
        roster: form.roster,
        rosterId,
        ownerUid: form.ownerUid,
        gameType: form.gameType,
        ...memberFields,
        creditsLeft: creditsLeft,
        totalCost: totalCost,
        initialCredits: factionBudget,
        ...(lineupUpdate || {}),
        updatedAt: serverTimestamp()
      }

      if (editingId) {
        await updateDoc(doc(db, 'factions', editingId), payload)
        await updateDoc(doc(db, 'users', form.ownerUid), {
          factionId: editingId,
          factionIds: arrayUnion(editingId)
        })
        setMessage('Fazione aggiornata con successo!')
      } else {
        const newFactionRef = await addDoc(collection(db, 'factions'), {
          ...payload,
          createdAt: serverTimestamp(),
          malus: 0,
          accumulatedPoints: 0
        })

        await updateDoc(doc(db, 'users', form.ownerUid), {
          factionId: newFactionRef.id,
          factionIds: arrayUnion(newFactionRef.id)
        })

        setMessage('Fazione creata con successo!')
      }

      onSuccess()
    } catch (err) {
      setMessage(
        'Errore nel salvataggio: ' + (err?.message || 'Errore sconosciuto')
      )
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm("Confermi eliminazione? L'operazione è irreversibile."))
      return
    setSubmitting(true)
    try {
      await deleteDoc(doc(db, 'factions', editingId))
      await updateDoc(doc(db, 'users', form.ownerUid), {
        factionId: null,
        factionIds: arrayRemove(editingId)
      })
      setMessage('Fazione eliminata con successo!')
      onSuccess()
    } catch (err) {
      setMessage(
        "Errore nell'eliminazione: " + (err?.message || 'Errore sconosciuto')
      )
    } finally {
      setSubmitting(false)
    }
  }

  const owner = allUsers.find(u => u.id === initialOwnerId)
  const canOwnerCreateFaction = editingId || canCreateFaction(owner?.email)

  // Funzione per ottenere il colore del brand
  const getBrandColor = brand => {
    const brandColors = {
      SmackDown: 'bg-sd',
      Raw: 'bg-raw',
      'Free Agent': 'bg-light text-dark',
      NXT: 'bg-nxt'
    }
    return brandColors[brand] || 'bg-secondary'
  }

  // Wrestlers disponibili (escludendo quelli già selezionati e mostrando solo active = true)
  const renderWrestlerAvatar = (wrestler, size = 'default') =>
    wrestler?.imageUrl ? (
      <img
        src={wrestler.imageUrl}
        alt={wrestler.name}
        className={`rounded-circle bg-secondary manage-roster-faction-form__avatar ${
          size === 'sm' ? 'manage-roster-faction-form__avatar--sm' : ''
        }`}
      />
    ) : (
      <span
        className={`rounded-circle bg-secondary manage-roster-faction-form__avatar manage-roster-faction-form__avatar--placeholder ${
          size === 'sm' ? 'manage-roster-faction-form__avatar--sm' : ''
        }`}
        aria-hidden='true'
      >
        <i className='bi bi-person-fill'></i>
      </span>
    )

  const renderWrestlerBadges = wrestler => (
    <div className='d-flex align-items-center gap-2 mt-1 flex-wrap'>
      {wrestler?.alignment === 'face' ? (
        <i className='bi bi-emoji-smile text-success' title='Face'></i>
      ) : wrestler?.alignment === 'heel' ? (
        <i className='bi bi-emoji-angry-fill text-danger' title='Heel'></i>
      ) : (
        <span className='badge bg-secondary'>N/A</span>
      )}
      {wrestler?.brand && (
        <span className={`badge ${getBrandColor(wrestler.brand)}`}>
          {wrestler.brand}
        </span>
      )}
      <span className='badge bg-light text-dark border'>
        {wrestler?.score || 0} pt.
      </span>
    </div>
  )

  const renderCostControl = (cost, originalIdx, wrestlerName) => (
    <div className='input-group input-group-sm manage-roster-faction-form__cost-control'>
      <button
        type='button'
        className='btn btn-outline-light'
        onClick={() => adjustCost(originalIdx, -1)}
        disabled={submitting || normalizeCost(cost.cost) <= 1}
        aria-label={`Diminuisci costo ${wrestlerName}`}
      >
        <i className='bi bi-dash-lg'></i>
      </button>
      <span className='input-group-text'>
        <CreditIcon size={16} />
      </span>
      <input
        type='number'
        className='form-control text-center py-2'
        value={cost.cost}
        onChange={e => handleCostChange(originalIdx, e.target.value)}
        min='1'
        step='1'
        inputMode='numeric'
        pattern='[0-9]*'
        disabled={submitting}
        aria-label={`Costo ${wrestlerName}`}
        style={{
          fontSize: '16px' // Previene lo zoom automatico su iOS
        }}
      />
      <button
        type='button'
        className='btn btn-outline-light'
        onClick={() => adjustCost(originalIdx, 1)}
        disabled={submitting}
        aria-label={`Aumenta costo ${wrestlerName}`}
      >
        <i className='bi bi-plus-lg'></i>
      </button>
    </div>
  )

  const availableWrestlers = useMemo(
    () =>
      getRosterAvailableWrestlers({
        wrestlers,
        factions,
        excludedFactionId: editingId,
        selectedWrestlerIds,
        onlyMainRoster,
        onlyNXT
      }),
    [
      editingId,
      factions,
      onlyMainRoster,
      onlyNXT,
      selectedWrestlerIds,
      wrestlers
    ]
  )

  // Wrestlers filtrati per ricerca
  const filteredWrestlers = useMemo(
    () =>
      availableWrestlers.filter(w =>
        w.name.toLowerCase().includes(searchWrestler.toLowerCase())
      ),
    [availableWrestlers, searchWrestler]
  )

  return (
    <form onSubmit={handleSubmit} className='manage-roster-faction-form'>
      {!canOwnerCreateFaction && (
        <SeasonCreationBanner
          userEmail={owner?.email}
          className='mb-4'
          compact
        />
      )}

      <div className='manage-roster-faction-form__summary'>
        <div>
          <small>Slot disponibili</small>
          <strong>
            {selectedCount}/{maxSlots}
          </strong>
        </div>
        <div>
          <small>Crediti residui</small>
          <strong>{creditsLeft}</strong>
        </div>
      </div>

      {(minMaleWrestlers > 0 ||
        minFemaleWrestlers > 0 ||
        onlyMainRoster ||
        onlyNXT) && (
        <div className='alert alert-dark border border-brand-secondary mb-4'>
          <div className='d-flex flex-wrap gap-2 align-items-center'>
            {minMaleWrestlers > 0 && (
              <span
                className={`badge ${
                  selectedMaleCount >= minMaleWrestlers
                    ? 'bg-male'
                    : 'bg-secondary'
                }`}
              >
                Uomini: {selectedMaleCount}/{minMaleWrestlers}
              </span>
            )}
            {minFemaleWrestlers > 0 && (
              <span
                className={`badge ${
                  selectedFemaleCount >= minFemaleWrestlers
                    ? 'bg-female'
                    : 'bg-secondary'
                }`}
              >
                Donne: {selectedFemaleCount}/{minFemaleWrestlers}
              </span>
            )}
            {onlyMainRoster && (
              <span className='badge bg-brand-primary-light text-white'>
                Solo Raw / SmackDown
              </span>
            )}
            {onlyNXT && (
              <span className='badge bg-brand-secondary text-dark'>
                Solo NXT
              </span>
            )}
          </div>
        </div>
      )}

      {/* Nome Fazione */}
      <div className='mb-4'>
        <label className='form-label fw-bold'>
          <i className='bi bi-tag me-2'></i>
          Nome Fazione*
        </label>
        <input
          type='text'
          className='form-control form-control-lg'
          value={form.name}
          onChange={e => updateField('name', e.target.value)}
          required
          minLength={3}
          maxLength={50}
          disabled={submitting}
          placeholder='Inserisci nome della fazione'
        />
      </div>

      {/* Sezione Wrestler */}
      <div className='card border mb-4 manage-roster-subcard'>
        <div className='card-header d-flex justify-content-between align-items-center manage-roster-faction-form__card-header'>
          <h6 className='mb-0'>
            <i className='bi bi-people-fill me-2'></i>
            Wrestler ({selectedCount}/{maxSlots})
          </h6>
          {selectedCount > 0 && (
            <button
              type='button'
              className='btn btn-sm btn-outline-danger mt-1'
              onClick={() => {
                const newCosts = emptySlots.map(slot => ({ ...slot }))
                updateField('costs', newCosts)
              }}
              disabled={submitting}
            >
              <i className='bi bi-trash me-1'></i> Rimuovi tutti
            </button>
          )}
        </div>

        <div className='card-body'>
          {/* Wrestler selezionati */}
          {selectedCount > 0 ? (
            <>
              <div className='table-responsive mb-4 d-none d-md-block manage-roster-faction-form__selected-table'>
                <table className='table'>
                  <thead className='table-light'>
                    <tr>
                      <th>Wrestler</th>
                      <th width='190'>Costo</th>
                      <th width='80'>Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedCostRows.map(
                      ({ originalIdx, wrestler, ...cost }) => {
                        const wrestlerName =
                          wrestler?.name || 'Wrestler non trovato'

                        return (
                          <tr key={originalIdx}>
                            <td>
                              <div className='d-flex align-items-center gap-2'>
                                {renderWrestlerAvatar(wrestler, 'sm')}
                                <div className='manage-roster-faction-form__wrestler-copy'>
                                  <div className='d-flex align-items-center'>
                                    <span className='fw-medium manage-roster-faction-form__wrestler-name'>
                                      {wrestlerName}
                                    </span>
                                    {wrestler?.isInjured && (
                                      <InjuryIcon
                                        size={18}
                                        iconSize='0.85rem'
                                        className='ms-2'
                                      />
                                    )}
                                  </div>
                                  {renderWrestlerBadges(wrestler)}
                                </div>
                              </div>
                            </td>
                            <td>
                              {renderCostControl(
                                cost,
                                originalIdx,
                                wrestlerName
                              )}
                            </td>
                            <td>
                              <button
                                type='button'
                                className='btn btn-sm btn-outline-danger'
                                onClick={() => clearWrestlerSlot(originalIdx)}
                                disabled={submitting}
                                title='Rimuovi wrestler'
                                aria-label={`Rimuovi ${wrestlerName}`}
                              >
                                <i className='bi bi-x-lg'></i>
                              </button>
                            </td>
                          </tr>
                        )
                      }
                    )}
                  </tbody>
                </table>
              </div>

              <div className='manage-roster-faction-form__selected-list d-md-none mb-4'>
                {selectedCostRows.map(({ originalIdx, wrestler, ...cost }) => {
                  const wrestlerName = wrestler?.name || 'Wrestler non trovato'

                  return (
                    <div
                      className='manage-roster-faction-form__selected-card'
                      key={originalIdx}
                    >
                      <div className='d-flex align-items-start gap-3'>
                        {renderWrestlerAvatar(wrestler)}
                        <div className='flex-grow-1 manage-roster-faction-form__wrestler-copy'>
                          <div className='d-flex align-items-start justify-content-between gap-2'>
                            <div>
                              <div className='fw-semibold manage-roster-faction-form__wrestler-name'>
                                {wrestlerName}
                              </div>
                              {renderWrestlerBadges(wrestler)}
                            </div>
                            {wrestler?.isInjured && (
                              <InjuryIcon
                                size={18}
                                iconSize='0.85rem'
                                className='mt-1 flex-shrink-0'
                              />
                            )}
                          </div>
                        </div>
                      </div>

                      <div className='manage-roster-faction-form__selected-card-actions'>
                        <div>
                          <label className='form-label small mb-1'>
                            Costo
                          </label>
                          {renderCostControl(cost, originalIdx, wrestlerName)}
                        </div>
                        <button
                          type='button'
                          className='btn btn-outline-danger'
                          onClick={() => clearWrestlerSlot(originalIdx)}
                          disabled={submitting}
                        >
                          <i className='bi bi-x-lg me-1'></i>
                          Rimuovi
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          ) : (
            <div className='alert alert-info mb-4'>
              Nessun wrestler selezionato. Puoi aggiungerne di nuovi qui sotto.
            </div>
          )}

          {/* Aggiungi nuovo wrestler - solo se ci sono slot disponibili */}
          {emptySlotsCount > 0 && (
            <div className='border-top pt-3'>
              <div className='d-flex justify-content-between align-items-center gap-2 flex-wrap mb-3'>
                <h6 className='mb-0'>
                  <i className='bi bi-person-plus me-2'></i>
                  Aggiungi wrestler
                </h6>
                <span className='badge bg-light text-dark border'>
                  {emptySlotsCount} slot liberi
                </span>
              </div>

              {/* Ricerca wrestler */}
              <div className='mb-3'>
                <div className='d-flex justify-content-between align-items-center gap-2 flex-wrap mb-2'>
                  <label className='form-label mb-0'>Cerca wrestler</label>
                  <small className='text-muted'>
                    {filteredWrestlers.length} disponibili
                  </small>
                </div>
                <input
                  type='search'
                  className='form-control form-control-lg'
                  placeholder='Cerca per nome...'
                  value={searchWrestler}
                  onChange={e => setSearchWrestler(e.target.value)}
                  disabled={submitting}
                  autoComplete='off'
                />
              </div>

              {/* Lista wrestler disponibili */}
              <div className='mb-3'>
                <label className='form-label'>Seleziona wrestler</label>
                <div className='list-group manage-roster-faction-form__available-list'>
                  {filteredWrestlers.length > 0 ? (
                    filteredWrestlers.map(wrestler => {
                      const emptySlotIdx = form.costs.findIndex(
                        c => c.wrestlerId === ''
                      )
                      return (
                        <div
                          key={wrestler.id}
                          className='list-group-item list-group-item-action d-flex justify-content-between align-items-center gap-3 manage-roster-faction-form__wrestler-option'
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            if (emptySlotIdx !== -1) {
                              assignWrestlerToSlot(
                                emptySlotIdx,
                                wrestler.id,
                                1
                              )
                              setSearchWrestler('')
                            }
                          }}
                        >
                          <div className='d-flex align-items-center gap-3 flex-grow-1 manage-roster-faction-form__wrestler-option-main'>
                            {renderWrestlerAvatar(wrestler)}
                            <div className='flex-grow-1 manage-roster-faction-form__wrestler-copy'>
                              <div className='d-flex align-items-center mb-1'>
                                <span className='fw-medium manage-roster-faction-form__wrestler-name'>
                                  {wrestler.name}
                                </span>
                                {wrestler.isInjured && (
                                  <InjuryIcon
                                    size={18}
                                    iconSize='0.85rem'
                                    className='ms-2'
                                  />
                                )}
                              </div>
                              {renderWrestlerBadges(wrestler)}
                            </div>
                          </div>
                          <button
                            type='button'
                            className='btn-brand-money manage-roster-faction-form__add-button'
                            onClick={e => {
                              e.stopPropagation()
                              if (emptySlotIdx !== -1) {
                                assignWrestlerToSlot(
                                  emptySlotIdx,
                                  wrestler.id,
                                  1
                                )
                                setSearchWrestler('')
                              }
                            }}
                            disabled={emptySlotIdx === -1 || submitting}
                            aria-label={`Aggiungi ${wrestler.name}`}
                          >
                            <i className='bi bi-plus-lg text-white'></i>
                            <span>Aggiungi</span>
                          </button>
                        </div>
                      )
                    })
                  ) : (
                    <div className='list-group-item text-center text-muted py-4'>
                      {searchWrestler
                        ? 'Nessun wrestler disponibile trovato'
                        : 'Tutti i wrestler attivi sono gia assegnati o selezionati'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Riepilogo */}
      <div className='card border mb-4 manage-roster-subcard'>
        <div className='card-header'>
          <h6 className='mb-0'>
            <i className='bi bi-calculator me-2'></i>
            Riepilogo
          </h6>
        </div>
        <div className='card-body'>
          <div className='row g-3 text-center'>
            <div className='col-12 col-sm-4'>
              <div className='bg-brand-primary-ultralight rounded p-3 text-brand-primary'>
                <div className='manage-roster-faction-form__summary-number fw-bold'>
                  {factionBudget}
                </div>
                <small>Crediti Iniziali</small>
              </div>
            </div>
            <div className='col-12 col-sm-4'>
              <div className='bg-brand-accent-light rounded p-3'>
                <div className='manage-roster-faction-form__summary-number fw-bold'>
                  {totalCost}
                </div>
                <small className='text-muted'>Costo Totale</small>
              </div>
            </div>
            <div className='col-12 col-sm-4'>
              <div
                className={`rounded p-3 ${
                  creditsLeft >= 0
                    ? 'bg-success bg-opacity-10'
                    : 'bg-danger bg-opacity-10'
                }`}
              >
                <div
                  className={`manage-roster-faction-form__summary-number fw-bold ${
                    creditsLeft >= 0 ? 'text-success' : 'text-danger'
                  }`}
                >
                  {creditsLeft}
                </div>
                <small className='text-muted'>Crediti Residui</small>
              </div>
            </div>
          </div>

          {creditsLeft < 0 && (
            <div className='alert alert-danger'>
              <i className='bi bi-exclamation-triangle-fill me-2'></i>
              Attenzione! I crediti sono insufficienti. Riduci il costo dei
              wrestler.
            </div>
          )}
        </div>
      </div>

      {/* Messaggi di stato */}
      {message && (
        <div
          className={`alert ${
            message.includes('success') ? 'alert-success' : 'alert-danger'
          } d-flex align-items-start gap-2 animate__animated animate__fadeIn`}
          aria-live='polite'
        >
          <i
            className={`bi ${
              message.includes('success')
                ? 'bi-check-circle-fill'
                : 'bi-exclamation-triangle-fill'
            } mt-1`}
          ></i>
          <div className='flex-grow-1'>{message}</div>
          <button
            type='button'
            className='btn-close'
            onClick={() => setMessage('')}
            aria-label='Chiudi'
          ></button>
        </div>
      )}

      {/* Pulsanti Azione */}
      <div className='d-flex justify-content-between align-items-center border-top pt-3 gap-3 manage-roster-faction-form__actions'>
        {/* Pulsanti sinistra */}
        <div className='d-flex gap-2 manage-roster-faction-form__actions-group'>
          {editingId && (
            <button
              type='button'
              className='btn btn-outline-danger'
              onClick={handleDelete}
              disabled={submitting}
            >
              <i className='bi bi-trash me-1'></i>
              Elimina Fazione
            </button>
          )}
          <button
            type='button'
            className='btn btn-outline-secondary'
            onClick={onCancel}
            disabled={submitting}
          >
            <i className='bi bi-x-circle me-1'></i>
            Annulla
          </button>
        </div>

        {/* Pulsanti destra */}
        <div className='d-flex gap-2 manage-roster-faction-form__actions-group'>
          {editingId && (
            <button
              type='button'
              className='btn btn-outline-primary'
              onClick={() => {
                if (existingFaction) {
                  setForm({
                    name: existingFaction.name || '',
                    roster: existingFaction.roster || initialRosterName,
                    ownerUid: existingFaction.ownerUid || initialOwnerId,
                    gameType: existingFaction.gameType || 'Competitivo',
                    costs: buildPaddedSlots(existingFaction)
                  })
                }
              }}
              disabled={submitting}
              title='Ripristina valori originali'
            >
              <i className='bi bi-arrow-counterclockwise me-1'></i>
              Ripristina
            </button>
          )}

          <button
            type='submit'
            className='btn-brand-primary px-4'
            disabled={creditsLeft < 0 || submitting || !canOwnerCreateFaction}
          >
            {submitting ? (
              <>
                <span
                  className='spinner-border spinner-border-sm me-2'
                  role='status'
                  aria-hidden='true'
                ></span>
                {editingId ? 'Salvataggio...' : 'Creazione...'}
              </>
            ) : (
              <>
                <i
                  className={`bi ${
                    editingId ? 'bi-save' : 'bi-check-circle'
                  } me-1`}
                ></i>
                {editingId ? 'Salva Modifiche' : 'Crea Fazione'}
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  )
}

FactionForm.propTypes = {
  initialOwnerId: PropTypes.string.isRequired,
  rosterId: PropTypes.string.isRequired,
  initialRosterName: PropTypes.string.isRequired,
  roster: PropTypes.object,
  maxWrestlers: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  initialCredits: PropTypes.number,
  minMaleWrestlers: PropTypes.number,
  minFemaleWrestlers: PropTypes.number,
  onlyMainRoster: PropTypes.bool,
  onlyNXT: PropTypes.bool,
  existingFaction: PropTypes.object,
  onSuccess: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  allUsers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      username: PropTypes.string,
      email: PropTypes.string
    })
  ).isRequired,
  wrestlers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      imageUrl: PropTypes.string,
      brand: PropTypes.string,
      alignment: PropTypes.string,
      score: PropTypes.number,
      isInjured: PropTypes.bool,
      active: PropTypes.bool
    })
  ).isRequired,
  factions: PropTypes.arrayOf(PropTypes.object).isRequired
}

export default FactionForm
